import { useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/use-toast';
import { cn } from 'lib/utils';
import DummyProfile from 'assets/images/dummy_profile.png';
import {
  accountGhostButtonClassName,
  accountOutlineButtonClassName,
  accountSectionClassName,
  accountSectionTitleClassName,
} from './account-styles';

// Same limit and file types as the Edit profile dialog.
const MAX_IMAGE_BYTES = 512 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

interface ProfilePictureCardProps {
  imageUrl?: string;
  isSaving: boolean;
  onChange: (profileImageUrl: string) => void;
}

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file as Base64'));
    reader.readAsDataURL(file);
  });

export function ProfilePictureCard({ imageUrl, isSaving, onChange }: ProfilePictureCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast({
        variant: 'destructive',
        title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
        description: t('UNSUPPORTED_FILE'),
      });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast({
        variant: 'destructive',
        title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
        description: `${t('MAX_SIZE_PROFILE_IMAGE')} (${file.name}) .`,
      });
      return;
    }

    onChange(await readAsDataUrl(file));
  };

  return (
    <section
      className={cn(
        accountSectionClassName,
        'flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center'
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <img
          src={imageUrl || DummyProfile}
          alt=""
          className="size-[72px] shrink-0 rounded-full object-cover"
        />
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className={accountSectionTitleClassName}>
            {t('ACCOUNT_PROFILE_PICTURE', { defaultValue: 'Profile picture' })}
          </h2>
          <p className="text-sm text-grey-500 dark:text-muted-foreground">
            {t('ACCOUNT_PROFILE_PICTURE_HINT', {
              defaultValue: 'PNG or JPG, up to 512 KB. Your team sees this.',
            })}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          disabled={isSaving}
          onClick={() => inputRef.current?.click()}
          className={accountOutlineButtonClassName}
        >
          {t('ACCOUNT_UPLOAD_PHOTO', { defaultValue: 'Upload photo' })}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          className="hidden"
          data-testid="profile-picture-input"
          onChange={handleFileChange}
        />
        {imageUrl ? (
          <button
            type="button"
            disabled={isSaving}
            onClick={() => onChange('')}
            className={accountGhostButtonClassName}
          >
            {t('ACCOUNT_REMOVE', { defaultValue: 'Remove' })}
          </button>
        ) : null}
      </div>
    </section>
  );
}
